/**
 * @namespace source.folders
 */

import { filter, find } from 'ramda';
import { systemFolderId } from '../../constants/system-folder-id';
import { isSameSite } from '../../utils/utils';
import { wixVideoLibrary, WIX_VIDEO_LIBRARY_LINK } from '../wix-video/data';
import { toFolder, toOldMediaFolder } from './mappers/folder';
import { siteToFolder } from './mappers/site-to-folder';
import { toError } from './parsers/error';
import { decodeItemId } from './parsers/item-id';
export function initFolders(settings, http, privateSiteFolders, wixVideoItems) {
  const defaultPaging = {
    page_size: 50,
    cursor: null
  };

  // @obsolete
  const fetchOldMediaFolders = (folderId, options) => {
    http.setRequestOptionsOnce({
      params: {
        site_token: null
      }
    });
    return http.get(`${settings.apiUrl}/go/folders`, {
      media_type: options.mediaType
    }, {
      cache: false,
      headers: options.headers || {}
    }).then(response => ({
      ...response,
      data: (response.data.folders || []).map(folder => toOldMediaFolder(folder, folderId))
    })).catch(toError);
  };
  const currentSiteFirst = sites => {
    const newSites = [...sites];
    const currentSite = find(site => isSameSite(settings.siteMediaToken, site.site_token), newSites);
    if (currentSite) {
      return [currentSite, ...filter(site => site.site_token !== currentSite.site_token, newSites)];
    }
    return sites;
  };
  const fetchSites = (options, query) => {
    options = options || {};
    const queryParams = {
      ...defaultPaging,
      ...options.paging
    };
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }

    // Add ns parameter for Wixel source
    if (settings.isWixel) {
      queryParams.ns = 'MEDIA_DESIGN_CREATOR';
    }
    if (options.includeCurrentSite) {
      queryParams.includeCurrentSite = true;
    }
    if (options.order) {
      const {
        order,
        direction
      } = options.order;
      queryParams.order = direction === 'desc' ? `-${order}` : order;
    }
    if (query) {
      queryParams.query = query;
    }
    return http.get(`${settings.apiUrl}/site/media/user/sites`, queryParams, {
      cache: false,
      headers: options.headers || {}
    }).then(response => ({
      ...response,
      data: currentSiteFirst(response.data.sites || []).map(siteToFolder),
      paging: {
        cursor: response.data.cursor
      }
    })).catch(toError);
  };

  /**
   * @summary Return the list of folders
   * @memberof source.folders
   * @param {String} folderId
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List[]} List with Folders
   * @reject {Error}
   */
  function list(folderId, options) {
    if (options === void 0) {
      options = {};
    }
    if (folderId === systemFolderId.WixSite) {
      return fetchSites(options);
    }
    const {
      id,
      siteMediaToken
    } = decodeItemId(folderId);
    if (!id && !siteMediaToken) {
      // All Site Files
      return fetchOldMediaFolders(folderId, options);
    }
    if (id === WIX_VIDEO_LIBRARY_LINK) {
      return wixVideoItems.list(siteMediaToken, options);
    }
    if (!siteMediaToken) {
      return Promise.resolve({
        data: []
      });
    }

    // hide contents of current site, no need to import to itself
    const topLevelSiteToken = settings.topLevelMediaToken || settings.siteMediaToken;
    const sameSites = isSameSite(topLevelSiteToken, siteMediaToken);
    return privateSiteFolders.list(id, {
      ...options,
      siteMediaToken
    }).then(response => {
      if (id === systemFolderId.SiteFiles) {
        return wixVideoItems.exist(siteMediaToken).then(existResponse => [existResponse, response]);
      }
      return Promise.resolve([false, response]);
    }).then(_ref => {
      let [hasWixVideoUploads, response] = _ref;
      return {
        ...response,
        data: [...(hasWixVideoUploads ? [wixVideoLibrary(siteMediaToken)] : []), ...(sameSites ? [] : response.data.map(folder => toFolder(folder, folderId)))]
      };
    });
  }

  /**
   * @summary Return the list of folders
   * @memberof source.folders
   * @param {String} folderId
   * @param {String} query
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List[]} List with Folders
   * @reject {Error}
   */
  function search(folderId, query, options) {
    if (folderId === systemFolderId.WixSite) {
      return fetchSites(options, query);
    }
    const {
      id,
      siteMediaToken
    } = decodeItemId(folderId);
    if (!siteMediaToken || id === WIX_VIDEO_LIBRARY_LINK) {
      return Promise.resolve({
        data: []
      });
    }
    return privateSiteFolders.search(id, query, {
      ...options,
      siteMediaToken
    }).then(response => ({
      ...response,
      data: response.data.map(folder => toFolder(folder, folderId))
    }));
  }
  return {
    list,
    search
  };
}